import styles from "./styles.module.scss";
import { FC, Props } from "./typings";
import { useEffect, useMemo, useRef, useState } from "react";
import tagsService from "api/tagsService";
import { ITag, TagType } from "dto/ITag";
import { getCompanyId as getWorkspaceId } from "utils/getCompanyId";

import taskService from "api/taskService";
import { ENotificationType, notification } from "utils/notification";
import { ITask } from "dto/ITask";
import TagBadge from "components/TagBadge";
import Input from "components/Input";
import NotFoundScreen from "components/NotFoundScreen";
import { useTranslation } from "react-i18next";

const ChooseTagNew: FC<Props> = ({
  label,
  updateTags,
  toTag,
  isTask = false,
  afterAddTag,
  afterRemoveTag,
  onChoose,
  type,

  variant = "filled",
  labelPosition = "top",
  disableBorder = false,
  className,
}) => {
  const { t } = useTranslation();
  const currentWorkspaceId = getWorkspaceId();
  const { data: tagsData, isLoading } = tagsService.useGetTags(
    false,
    type ?? TagType.Other,
    undefined,
    currentWorkspaceId ?? "",
  );

  const [tags, setTags] = useState<ITag[]>([]);
  const allTags = useMemo(() => tagsData?.data ?? [], [tagsData]);

  const [search, setSearch] = useState<ITag[]>([]);
  const [value, setValue] = useState("");

  const outsideRef = useRef(null);

  const { mutate: editTags } = tagsService.useAddTag();
  const { mutate: editTask } = taskService.useUpdateTask();

  const handleAddTag = (tag: ITag) => {
    if (!toTag) return;

    const newTags = [...tags, tag];

    editTags(
      {
        Items: [{ Id: toTag.Id, Kind: toTag.Kind }],
        NewTags: newTags
          .filter((tag) => tag.Id === "-1")
          .map((tag) => tag.Name),
        TagIds: newTags.filter((tag) => tag.Id !== "-1").map((tag) => tag.Id),
      },
      {
        onSuccess: (r) => {
          if (r.data) {
            updateTags?.(r.data.map((t: ITag) => t));
          }
        },
      },
    );
    afterAddTag?.(tag.Id);
    setTags(newTags);
    setValue("");
  };

  // useOnClickOutside(outsideRef, () => {
  //   if (active) {
  //     setActive(false);
  //   }

  //   if (onClose && !label) {
  //     onClose();
  //   }
  // });

  useEffect(() => {
    if (!value.length) {
      setSearch(
        allTags.filter((tag: ITag) => !tags.find((t) => t.Id == tag.Id)),
      );
      return;
    }

    setSearch(
      allTags.filter(
        (tag: ITag) =>
          tag.Name.toLocaleLowerCase().search(value.toLocaleLowerCase()) !==
            -1 && !tags.find((t) => t.Id == tag.Id),
      ),
    );
  }, [allTags, value, tags]);

  useEffect(() => {
    if (toTag) {
      console.log("All tags", allTags, toTag);
      setTags(allTags.filter((t: ITag) => (toTag.TagIds ?? []).includes(t.Id)));
    }
  }, [allTags, toTag]);

  const handleRemoveTag = (rtag: ITag) => {
    if (!toTag) return;

    const newTags = tags.filter((tag) => tag.Name !== rtag.Name);

    if (isTask) {
      const task = toTag as ITask;
      editTask({
        Id: task.Id,
        Allday: task.AllDay,
        FromDate: task.FromDate,
        IsFavourite: task.IsFavourite,
        Note: task.Note,
        OwnerId: task.OwnerId,
        OwnerKind: task.Kind,
        TagIds: newTags.filter((tag) => tag.Id !== "-1").map((tag) => tag.Id),
        ToDate: task.ToDate,
      });
      afterRemoveTag?.(rtag.Id);
      setTags(newTags);
      return;
    }

    editTags(
      {
        Items: [{ Id: toTag.Id, Kind: toTag.Kind }],
        NewTags: newTags
          .filter((tag) => tag.Id === "-1")
          .map((tag) => tag.Name),
        TagIds: newTags.filter((tag) => tag.Id !== "-1").map((tag) => tag.Id),
      },
      {
        onSuccess: (r) => {
          if (r.data) {
            updateTags?.(r.data.map((t: ITag) => t));
          }
        },
        onError: () => {
          notification({
            type: ENotificationType.ERROR,
            title: t("errors.occurred"),
            text: t("errors.tryAgainLater"),
          });
        },
      },
    );
    afterRemoveTag?.(rtag.Id);
    setTags(newTags);
  };

  return (
    <div ref={outsideRef} className={`${styles.wrapper} ${className}`}>
      <Input
        label={label ?? t("common.search")}
        onChange={(text) => {
          setValue(text);
        }}
        value={value}
        variant={variant}
        disableBorder={disableBorder}
        labelPosition={labelPosition}
        focus={true}
      />
      <div className={styles.choosen}>
        {tags.map((x) => (
          <TagBadge
            tag={x}
            onRemove={handleRemoveTag}
            key={`choosen_${x.Id}`}
          ></TagBadge>
        ))}
      </div>
      <div className={styles.list}>
        {search.map((tag: ITag) => (
          <div
            onClick={() => {
              if (toTag) handleAddTag(tag);
              else onChoose?.(tag);
            }}
            key={tag.Id}
            className={styles.box}
          >
            {tag.Name}
          </div>
        ))}
        {!isLoading && search.length === 0 && (
          <div className={styles.empty}>
            <NotFoundScreen
              title={t("emptyStates.notFound")}
              text={t("emptyStates.noResultsSuffix")}
              size="normal"
            ></NotFoundScreen>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChooseTagNew;
